import { useState, useEffect, useCallback, useRef } from 'react';
import { AppState } from '../types';
import { initialState } from '../data/initialData';
import { useAuth } from './useAuth';
import {
  loadUserData,
  saveProfile,
  saveGoal,
  deleteGoal,
  saveCheckIn,
  saveActionItem,
  saveFinancialTarget,
  saveChatMessage,
  saveNotificationSettings,
  initializeUserProfile,
  saveLifestylePreferences,
  saveLifeValues,
  saveLocationOption,
  deleteLocationOption,
  saveOnboardingStatus,
  savePrivacyPreferences,
  saveCommunicationStyle,
  saveFinancialBaseline,
  saveAgentPaused,
  DataResult,
} from '../lib/supabase/dataService';

// User-specific storage key to prevent cross-user data pollution
const getStorageKey = (userId: string) => `life-os-${userId}`;

const LAST_SYNCED_KEY = 'life-os-lastSyncedAt';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export interface SyncStatus {
  isLoading: boolean;
  isSyncing: boolean;
  lastSynced: Date | null;
  error: string | null;
  staleSync: boolean;
}

export function useSyncedState(): [
  AppState,
  (newState: AppState | ((prev: AppState) => AppState)) => void,
  SyncStatus
] {
  const { user } = useAuth();
  // Start with clean initial state - load user-specific data after auth
  const [state, setStateInternal] = useState<AppState>(initialState);

  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isLoading: true,
    isSyncing: false,
    lastSynced: null,
    error: null,
    staleSync: false,
  });

  // Track previous state for comparison
  const prevStateRef = useRef<AppState | null>(null);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load data from Supabase when user logs in
  useEffect(() => {
    async function loadData() {
      if (!user) {
        setSyncStatus(prev => ({ ...prev, isLoading: false }));
        return;
      }

      setSyncStatus(prev => ({ ...prev, isLoading: true, error: null }));

      // Try to load user-specific localStorage cache first
      const storageKey = getStorageKey(user.id);
      let cachedState: Partial<AppState> | null = null;
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          cachedState = JSON.parse(saved);
        }
      } catch (e) {
        console.error('Error loading state from localStorage:', e);
      }

      const result = await loadUserData(user.id);

      if (!result.success) {
        // Error loading data from Supabase - use cache if available
        if (cachedState) {
          setStateInternal(prev => ({
            ...prev,
            ...cachedState,
            userPlaybooks: cachedState?.userPlaybooks || [],
          }));
        }
        setSyncStatus(prev => ({
          ...prev,
          isLoading: false,
          error: result.error || 'Failed to load data from server. Using cached data.',
        }));
        return;
      }

      if (result.data) {
        const loadedData = result.data;
        // Merge with existing state, preferring Supabase data
        setStateInternal(prev => ({
          ...prev,
          ...loadedData,
          // Keep userPlaybooks from localStorage until we add the table
          userPlaybooks: cachedState?.userPlaybooks || prev.userPlaybooks || [],
          // Ensure life planning data is initialized
          lifestylePreferences: loadedData.lifestylePreferences || prev.lifestylePreferences || initialState.lifestylePreferences,
          lifeValues: loadedData.lifeValues || prev.lifeValues || initialState.lifeValues,
          locationOptions: loadedData.locationOptions || prev.locationOptions || [],
        }));
        localStorage.setItem(LAST_SYNCED_KEY, new Date().toISOString());
        setSyncStatus(prev => ({
          ...prev,
          isLoading: false,
          lastSynced: new Date(),
          staleSync: false,
        }));
      } else {
        // New user - initialize their profile
        const userName = user.user_metadata?.name || user.email?.split('@')[0] || 'User';
        const initResult = await initializeUserProfile(user.id, userName);

        if (!initResult.success) {
          setSyncStatus(prev => ({
            ...prev,
            isLoading: false,
            error: initResult.error || 'Failed to create profile.',
          }));
          return;
        }

        // Use initial state but update profile name - this is a fresh user
        setStateInternal(() => ({
          ...initialState,
          profile: {
            ...initialState.profile,
            name: userName,
          },
          onboardingComplete: false,
          userPlaybooks: [],
          lifestylePreferences: initialState.lifestylePreferences,
          lifeValues: initialState.lifeValues,
          locationOptions: [],
        }));
        localStorage.setItem(LAST_SYNCED_KEY, new Date().toISOString());
        setSyncStatus(prev => ({
          ...prev,
          isLoading: false,
          lastSynced: new Date(),
          staleSync: false,
        }));
      }

      // Check for stale sync after loading completes
      const lastSyncedStr = localStorage.getItem(LAST_SYNCED_KEY);
      if (lastSyncedStr) {
        const lastSyncedTime = new Date(lastSyncedStr).getTime();
        if (Date.now() - lastSyncedTime > SEVEN_DAYS_MS) {
          setSyncStatus(prev => ({ ...prev, staleSync: true }));
        }
      }
    }

    loadData();
  }, [user]);

  // Save to user-specific localStorage whenever state changes
  useEffect(() => {
    if (!user) return; // Don't save without user context
    try {
      const storageKey = getStorageKey(user.id);
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (e) {
      console.error('Error saving state to localStorage:', e);
    }
  }, [state, user]);

  // Debounced sync to Supabase
  const syncToSupabase = useCallback(async (currentState: AppState, prevState: AppState | null) => {
    if (!user) return;

    setSyncStatus(prev => ({ ...prev, isSyncing: true }));

    try {
      const promises: Promise<DataResult>[] = [];

      // Check what changed and sync only those parts
      if (!prevState || JSON.stringify(currentState.profile) !== JSON.stringify(prevState.profile)) {
        promises.push(saveProfile(user.id, currentState.profile));
      }

      // Sync goals
      if (!prevState || JSON.stringify(currentState.goals) !== JSON.stringify(prevState.goals)) {
        // Find new or updated goals
        for (const goal of currentState.goals) {
          const prevGoal = prevState?.goals.find(g => g.id === goal.id);
          if (!prevGoal || JSON.stringify(goal) !== JSON.stringify(prevGoal)) {
            promises.push(saveGoal(user.id, goal));
          }
        }
        // Find deleted goals
        if (prevState) {
          for (const prevGoal of prevState.goals) {
            if (!currentState.goals.find(g => g.id === prevGoal.id)) {
              promises.push(deleteGoal(user.id, prevGoal.id));
            }
          }
        }
      }

      // Sync daily check-ins (only new ones)
      if (!prevState || currentState.dailyCheckIns.length !== prevState.dailyCheckIns.length) {
        const newCheckIns = currentState.dailyCheckIns.filter(
          c => !prevState?.dailyCheckIns.find(pc => pc.id === c.id)
        );
        for (const checkIn of newCheckIns) {
          promises.push(saveCheckIn(user.id, checkIn));
        }
      }

      // Sync action items (status changes)
      if (!prevState || JSON.stringify(currentState.actionItems) !== JSON.stringify(prevState.actionItems)) {
        for (const item of currentState.actionItems) {
          const prevItem = prevState?.actionItems.find(i => i.id === item.id);
          if (!prevItem || JSON.stringify(item) !== JSON.stringify(prevItem)) {
            promises.push(saveActionItem(user.id, item));
          }
        }
      }

      // Sync financial targets
      if (!prevState || JSON.stringify(currentState.financialTargets) !== JSON.stringify(prevState.financialTargets)) {
        for (const target of currentState.financialTargets) {
          const prevTarget = prevState?.financialTargets.find(t => t.id === target.id);
          if (!prevTarget || JSON.stringify(target) !== JSON.stringify(prevTarget)) {
            promises.push(saveFinancialTarget(user.id, target));
          }
        }
      }

      // Sync chat messages (only new ones)
      if (!prevState || currentState.chatHistory.length !== prevState.chatHistory.length) {
        const newMessages = currentState.chatHistory.filter(
          m => !prevState?.chatHistory.find(pm => pm.id === m.id)
        );
        for (const message of newMessages) {
          promises.push(saveChatMessage(user.id, message));
        }
      }

      // Sync notification settings
      if (!prevState || JSON.stringify(currentState.notificationSettings) !== JSON.stringify(prevState.notificationSettings)) {
        promises.push(saveNotificationSettings(user.id, currentState.notificationSettings));
      }

      // Sync lifestyle preferences
      if (!prevState || JSON.stringify(currentState.lifestylePreferences) !== JSON.stringify(prevState.lifestylePreferences)) {
        promises.push(saveLifestylePreferences(user.id, currentState.lifestylePreferences));
      }

      // Sync life values
      if (!prevState || JSON.stringify(currentState.lifeValues) !== JSON.stringify(prevState.lifeValues)) {
        promises.push(saveLifeValues(user.id, currentState.lifeValues));
      }

      // Sync location options
      if (!prevState || JSON.stringify(currentState.locationOptions) !== JSON.stringify(prevState.locationOptions)) {
        // Find new or updated locations
        for (const location of currentState.locationOptions) {
          const prevLocation = prevState?.locationOptions?.find(l => l.id === location.id);
          if (!prevLocation || JSON.stringify(location) !== JSON.stringify(prevLocation)) {
            promises.push(saveLocationOption(user.id, location));
          }
        }
        // Find deleted locations
        if (prevState?.locationOptions) {
          for (const prevLocation of prevState.locationOptions) {
            if (!currentState.locationOptions.find(l => l.id === prevLocation.id)) {
              promises.push(deleteLocationOption(user.id, prevLocation.id));
            }
          }
        }
      }

      // Sync onboarding status
      if (!prevState || currentState.onboardingComplete !== prevState.onboardingComplete) {
        promises.push(saveOnboardingStatus(user.id, currentState.onboardingComplete ?? false));
      }

      // Sync privacy preferences
      if (!prevState || JSON.stringify(currentState.privacyPreferences) !== JSON.stringify(prevState.privacyPreferences)) {
        promises.push(savePrivacyPreferences(user.id, currentState.privacyPreferences));
      }

      // Sync communication style
      if (!prevState || JSON.stringify(currentState.communicationStyle) !== JSON.stringify(prevState.communicationStyle)) {
        promises.push(saveCommunicationStyle(user.id, currentState.communicationStyle));
      }

      // Sync financial baseline
      if (currentState.financialBaseline && (!prevState || JSON.stringify(currentState.financialBaseline) !== JSON.stringify(prevState.financialBaseline))) {
        promises.push(saveFinancialBaseline(user.id, currentState.financialBaseline));
      }

      // Sync agent paused state
      if (!prevState || currentState.agentPaused !== prevState.agentPaused) {
        promises.push(saveAgentPaused(user.id, currentState.agentPaused));
      }

      const results = await Promise.all(promises);

      // Check for any failures
      const failures = results.filter(r => !r.success);
      if (failures.length > 0) {
        const firstError = failures[0].error || 'Some changes failed to sync.';
        setSyncStatus(prev => ({
          ...prev,
          isSyncing: false,
          error: firstError,
        }));
        return;
      }

      localStorage.setItem(LAST_SYNCED_KEY, new Date().toISOString());
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        lastSynced: new Date(),
        error: null,
        staleSync: false,
      }));
    } catch (error) {
      console.error('Error syncing to Supabase:', error);
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: 'Failed to sync changes. They will be saved locally.',
      }));
    }
  }, [user]);

  // Debounced state setter that triggers sync
  const setState = useCallback((newState: AppState | ((prev: AppState) => AppState)) => {
    setStateInternal(prevState => {
      const nextState = typeof newState === 'function' ? newState(prevState) : newState;

      // Clear existing timeout
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      // Debounce sync by 1 second
      syncTimeoutRef.current = setTimeout(() => {
        syncToSupabase(nextState, prevStateRef.current);
        prevStateRef.current = nextState;
      }, 1000);

      return nextState;
    });
  }, [syncToSupabase]);

  // Initialize prevStateRef after initial load
  useEffect(() => {
    if (!syncStatus.isLoading && prevStateRef.current === null) {
      prevStateRef.current = state;
    }
  }, [syncStatus.isLoading, state]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return [state, setState, syncStatus];
}
